// Lo que el cajón de Clientes arma con el historial de compras: mix por categoría y los
// desgloses por vendedor y por tienda. Lógica pura, sin React.
import type {
    BackofficeCustomerPurchase,
    ProductCategory,
    PurchaseLine,
} from '@/api/services/retail-customer/backoffice';
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import {
    hasEquipment,
    lineCategoryKey,
    ticketHeadline,
    type ExpandableTicketSale,
    type LineCategoryKey,
    type TicketCategory,
} from '@/shared/patterns/SaleTicket';

/** Las tarjetas del mix. `PREPAID` no tiene categoría en el catálogo y va sin dato. */
export type MixKey = 'ALL' | 'PHONES' | 'SIMS' | 'ACCESSORIES' | 'SERVICES' | 'PREPAID';

export const MIX_KEYS: readonly MixKey[] = [
    'ALL',
    'PHONES',
    'SIMS',
    'ACCESSORIES',
    'SERVICES',
    'PREPAID',
];

const CATEGORY_MIX: Record<ProductCategory, MixKey | null> = {
    PHONE: 'PHONES',
    SIM: 'SIMS',
    MODEM: 'SIMS',
    ACCESSORY: 'ACCESSORIES',
    BUNDLE: null,
};

/** Tarjeta de cada categoría de línea; los kits y la línea sin categoría no tienen. */
const LINE_MIX: Record<LineCategoryKey, MixKey | null> = {
    ...CATEGORY_MIX,
    SERVICE: 'SERVICES',
    UNCATEGORIZED: null,
};

export interface MixSlice {
    key: MixKey;
    /** Ventas que traen al menos una pieza de la categoría. `null` = sin fuente. */
    salesCount: number | null;
    amountCents: number | null;
}

/** Tarjeta de la línea con la categoría del ticket compartido: un servicio va en «Servicios». */
function lineMixKey(purchase: BackofficeCustomerPurchase, line: PurchaseLine, index: number) {
    return LINE_MIX[lineCategoryKey(toTicketLine(purchase, line, index))];
}

/** Las tarjetas de una compra: las de sus líneas, o la del producto principal si no trae líneas. */
function mixKeysOf(purchase: BackofficeCustomerPurchase): Set<MixKey> {
    if (purchase.lines.length === 0) {
        const key = purchase.category === null ? null : CATEGORY_MIX[purchase.category];
        return new Set(key ? [key] : []);
    }
    const keys = purchase.lines.map((line, index) => lineMixKey(purchase, line, index));
    return new Set(keys.filter((key) => key !== null));
}

/** Monto de la categoría dentro de la compra: sus líneas, o la venta entera si no trae líneas. */
function amountIn(purchase: BackofficeCustomerPurchase, key: MixKey): number {
    if (purchase.lines.length === 0) return purchase.amountCents;
    return purchase.lines
        .filter((line, index) => lineMixKey(purchase, line, index) === key)
        .reduce((sum, line) => sum + line.amountCents, 0);
}

export function categoryMix(purchases: BackofficeCustomerPurchase[]): MixSlice[] {
    return MIX_KEYS.map((key) => {
        if (key === 'PREPAID') return { key, salesCount: null, amountCents: null };
        if (key === 'ALL') {
            return {
                key,
                salesCount: purchases.length,
                amountCents: purchases.reduce((sum, p) => sum + p.amountCents, 0),
            };
        }
        const matching = purchases.filter((p) => mixKeysOf(p).has(key));
        return {
            key,
            salesCount: matching.length,
            amountCents: matching.reduce((sum, p) => sum + amountIn(p, key), 0),
        };
    });
}

export function purchasesInMix(
    purchases: BackofficeCustomerPurchase[],
    key: MixKey,
): BackofficeCustomerPurchase[] {
    if (key === 'ALL') return purchases;
    return purchases.filter((p) => mixKeysOf(p).has(key));
}

/** Ventas sin ninguna categoría del mix (kits o ventas sin líneas): solo cuentan en «Todos». */
export function uncategorizedSales(purchases: BackofficeCustomerPurchase[]): number {
    return purchases.filter((p) => mixKeysOf(p).size === 0).length;
}

const MIX_TICKET_CATEGORY: Record<MixKey, TicketCategory> = {
    ALL: null,
    PHONES: 'PHONE',
    SIMS: 'SIM',
    ACCESSORIES: 'ACCESSORY',
    SERVICES: 'SERVICE',
    PREPAID: null,
};

/** Categoría con la que el ticket compartido encabeza y resalta según la tarjeta del mix. */
export function mixTicketCategory(mix: MixKey): TicketCategory {
    return MIX_TICKET_CATEGORY[mix];
}

/** La compra en la forma del ticket compartido: sku `null` sin teléfono y líneas como `SaleLine`. */
export type CustomerTicketRow = Omit<BackofficeCustomerPurchase, 'folio' | 'lines'> &
    ExpandableTicketSale & { folio: string; lines: SaleLine[] };

/** Producto de una línea vieja que solo trae categoría; la del SKU principal toma el nombre de la venta. */
function fallbackProduct(
    purchase: BackofficeCustomerPurchase,
    line: PurchaseLine,
): SaleLine['product'] {
    if (line.category === null) return null;
    const isMain = line.sku === purchase.sku && purchase.productName !== '';
    return {
        brand: '',
        model: '',
        capacity: null,
        color: null,
        imageUrl: null,
        tier: null,
        category: line.category,
        trackingMode: 'QUANTITY',
        ...(isMain && { description: purchase.productName }),
    };
}

/** Línea del historial como `SaleLine`; sin precio del silo (línea vieja) reparte el monto por pieza. */
function toTicketLine(
    purchase: BackofficeCustomerPurchase,
    line: PurchaseLine,
    index: number,
): SaleLine {
    const qty = Math.max(1, line.qty);
    return {
        lineId: line.lineId ?? `${line.sku}#${String(index)}`,
        sku: line.sku,
        qty,
        basePriceCents: line.basePriceCents ?? line.amountCents / qty,
        ...(line.discountCents !== undefined && { discountCents: line.discountCents }),
        ...(line.paymentType && { paymentType: line.paymentType }),
        components: [],
        product: line.product ?? fallbackProduct(purchase, line),
        ...(line.role && { role: line.role }),
    };
}

export function toTicketRow(purchase: BackofficeCustomerPurchase): CustomerTicketRow {
    return {
        ...purchase,
        sku: hasEquipment(purchase) ? purchase.sku : null,
        folio: purchase.folio ?? '',
        lines: purchase.lines.map((line, index) => toTicketLine(purchase, line, index)),
    };
}

/** Nombre que encabeza la fila según la tarjeta del mix; también ordena la columna Producto. */
export function ticketRowName(row: CustomerTicketRow, mix: MixKey): string {
    return ticketHeadline(row, mixTicketCategory(mix), row.productName).name;
}

export function distinctSkus(purchases: BackofficeCustomerPurchase[]): number {
    const skus = new Set<string>();
    for (const p of purchases) {
        if (p.lines.length === 0 && hasEquipment(p)) skus.add(p.sku);
        for (const line of p.lines) skus.add(line.sku);
    }
    return skus.size;
}

/** Un renglón de los desgloses por vendedor o por tienda. */
export interface Breakdown {
    key: string;
    name: string;
    /** Tienda del vendedor; vacía en el desglose por tienda. */
    storeName: string | null;
    count: number;
    amountCents: number;
    /** Porcentaje entero de las compras del cliente. */
    pct: number;
    lastAt: string;
}

function breakdown(
    purchases: BackofficeCustomerPurchase[],
    keyOf: (p: BackofficeCustomerPurchase) => { key: string; name: string } | null,
    storeOf: (p: BackofficeCustomerPurchase) => string | null,
): Breakdown[] {
    const groups = new Map<string, Breakdown>();
    for (const p of purchases) {
        const id = keyOf(p);
        if (id === null) continue;
        const slot = groups.get(id.key) ?? {
            key: id.key,
            name: id.name,
            storeName: storeOf(p),
            count: 0,
            amountCents: 0,
            pct: 0,
            lastAt: p.soldAt,
        };
        slot.count += 1;
        slot.amountCents += p.amountCents;
        if (p.soldAt > slot.lastAt) slot.lastAt = p.soldAt;
        groups.set(id.key, slot);
    }
    const total = purchases.length;
    return [...groups.values()].map((g) => ({
        ...g,
        pct: total > 0 ? Math.round((g.count / total) * 100) : 0,
    }));
}

export function bySeller(purchases: BackofficeCustomerPurchase[]): Breakdown[] {
    return breakdown(
        purchases,
        (p) => {
            const key = p.sellerId ?? p.sellerName;
            return key ? { key, name: p.sellerName ?? key } : null;
        },
        (p) => p.storeName,
    );
}

export function byStore(purchases: BackofficeCustomerPurchase[]): Breakdown[] {
    return breakdown(
        purchases,
        (p) => {
            const key = p.storeId ?? p.storeName;
            return key ? { key, name: p.storeName ?? key } : null;
        },
        () => null,
    );
}

export type Direction = 'asc' | 'desc';

export interface TableSort<K extends string> {
    key: K;
    direction: Direction;
}

/** Repetir la columna invierte; una nueva arranca A→Z en texto y de mayor a menor en cifras. */
export function toggleSort<K extends string>(
    current: TableSort<K>,
    key: K,
    textKeys: readonly K[],
): TableSort<K> {
    if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { key, direction: textKeys.includes(key) ? 'asc' : 'desc' };
}

export function sortBy<T, K extends string>(
    rows: readonly T[],
    sort: TableSort<K>,
    valueOf: (row: T, key: K) => string | number,
): T[] {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const va = valueOf(a, sort.key);
        const vb = valueOf(b, sort.key);
        const diff =
            typeof va === 'string' && typeof vb === 'string'
                ? va.localeCompare(vb, 'es-MX')
                : Number(va) - Number(vb);
        return sign * diff;
    });
}
